import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ValidationError } from '@lender/utils';
import AutocompleteCDS, {
  onAutocompleteChangeInForm,
} from '@sharedCR/coreDesignSystem/form/AutocompleteCDS';
import ModalFormWrapper, { ModalFormLayout } from '@sharedCR/coreDesignSystem/ModalFormWrapper';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { object } from 'yup';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';

import { closeDialogAction } from 'store/global/actions';
import { getLoanApplicationTagsAction } from 'store/loanApplications/actions';
import { selectApplicationLoanTagsOptions } from 'store/loanApplications/selectors';

import { PropertyAndLoanTestIds } from '../../../../../PropertyAndLoan.testIds';

const schema = object().shape({
  loanTag: object().required(ValidationError.RequiredField).nullable(),
});

const LoanTagsAssignmentForm = ({
  loanTagsToExclude,
  isLoading,
  setIsLoading,
  dialogId,
  onAssignLoanTag,
}) => {
  const dispatch = useDispatch();

  const [isError, setIsError] = useState(false);

  const loanTagsOptions = useSelector(selectApplicationLoanTagsOptions(loanTagsToExclude));

  const { form, isFormChanged, validationErrors, setSchema, setInForm, validateField } = useForm({
    loanTag: null,
  });

  const fetchData = useCallback(() => {
    setIsLoading(true);
    setIsError(false);

    dispatch(getLoanApplicationTagsAction())
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));
  }, [dispatch, setIsLoading]);

  useEffectSafeStrictMode(() => {
    if (loanTagsOptions.length) {
      return;
    }

    fetchData();
  }, [fetchData, loanTagsOptions.length]);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  const onClose = useCallback(() => {
    dispatch(closeDialogAction(dialogId));
  }, [dispatch, dialogId]);

  const onSubmit = useCallback(() => {
    onAssignLoanTag(form.loanTag);
    onClose();
  }, [form.loanTag, onAssignLoanTag, onClose]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  return (
    <ModalFormWrapper
      fetchData={fetchData}
      isError={isError}
      isFormChanged={isFormChanged}
      isSubmitButtonDisabled={isLoading}
      layout={ModalFormLayout.EMBEDDED}
      submitButtonName="Select"
      onClose={onClose}
      onSubmit={onSubmit}
    >
      <AutocompleteCDS
        required
        dataTestId={PropertyAndLoanTestIds.LOAN_TAG_ASSIGNMENT_FORM_TAG_SELECT}
        disabled={isLoading}
        error={!!validationErrors.loanTag}
        helperText={validationErrors.loanTag}
        label="Loan Tag"
        name="loanTag"
        options={loanTagsOptions}
        placeholder="Select one"
        value={form.loanTag}
        onChange={onAutocompleteChange}
      />
    </ModalFormWrapper>
  );
};

LoanTagsAssignmentForm.propTypes = {
  ...WithLoadingPropTypes,
  loanTagsToExclude: PropTypes.array.isRequired,
  dialogId: PropTypes.string.isRequired,
};

export default withLoading(LoanTagsAssignmentForm);
